import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import api from "../../lib/api";
import { useFetch } from "../../hooks/useFetch";
import { Loading } from "../../components/States";
import { toast } from "sonner";
import { BUSINESS } from '../../lib/business';
import { FileWorkspace } from '../../components/FileWorkspace';
import {
  ArrowLeft, MapPin, Navigation, User, Phone, Camera, Check, Send,
  FileSignature, Package, ClipboardCheck, AlertTriangle, X,
} from "lucide-react";

export default function FieldJobDetail() {
  const { id } = useParams();
  const { data, loading } = useFetch(`/jobs/${id}`);
  const navigate = useNavigate();
  const [tasks, setTasks] = useState([]);
  const [photos, setPhotos] = useState([]);
  const [coOpen, setCoOpen] = useState(false);
  const [coDesc, setCoDesc] = useState("");
  const [coAmt, setCoAmt] = useState("");

  useEffect(() => {
    if (data) {
      setTasks(data.tasks || []);
      setPhotos(data.photos || []);
    }
  }, [data]);

  if (loading || !data) return <Loading />;

  const toggle = (idx) => setTasks((t) => t.map((x, i) => (i === idx ? { ...x, done: !x.done } : x)));
  const allDone = tasks.length > 0 && tasks.every((t) => t.done);

  const save = async (status) => {
    try {
      await api.post(`/jobs/${id}/submit`, { tasks, photos, status });
      toast.success(status === "submitted" ? "Job submitted! Office notified." : "Progress saved");
      if (status === "submitted") navigate("/field");
    } catch { toast.error("Could not save"); }
  };

  const submitCO = async () => {
    if (!coDesc.trim()) return;
    try {
      await api.post("/change-orders", { lot_id: data.lot_id, description: coDesc, amount: Number(coAmt) || 0 });
      toast.success("Field change created — sent to PM for pricing & approval");
      setCoOpen(false); setCoDesc(""); setCoAmt("");
    } catch { toast.error("Could not create field change"); }
  };

  const mapUrl = `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(data.address)}`;

  return (
    <div className="space-y-5">
      <button onClick={() => navigate(-1)} className="flex items-center gap-2 text-sm text-slate-400" data-testid="back-job">
        <ArrowLeft size={16} /> Back
      </button>

      <div>
        <span className="rounded-full bg-slate-800 px-3 py-1 text-xs font-semibold text-slate-300">{data.phase}</span>
        <h1 className="mt-3 font-head text-3xl font-black text-white">{data.title}</h1>
        <div className="mt-2 flex items-center gap-1.5 text-slate-400"><MapPin size={15} /> {data.address}</div>
      </div>

      <a href={mapUrl} target="_blank" rel="noreferrer" className="glove-btn flex w-full items-center justify-center gap-2 bg-cyan-500 text-slate-900 hover:bg-cyan-400" data-testid="directions-btn">
        <Navigation size={20} /> Directions
      </a>

      <div className="hw-card p-5">
        <div className="hw-eyebrow mb-2">Scope</div>
        <p className="text-slate-200">{data.scope}</p>
        {data.inspection_required && (
          <div className="mt-3 flex items-center gap-2 rounded-lg border border-amber-500/30 bg-amber-500/5 px-3 py-2 text-sm text-amber-300">
            <AlertTriangle size={15} /> Inspection required at completion
          </div>
        )}
      </div>

      <div className="hw-card p-5">
        <div className="mb-3 flex items-center gap-2"><User size={16} className="text-cyan-400" /><span className="font-semibold text-white">Supervisor</span></div>
        <div className="flex items-center justify-between">
          <span className="text-slate-300">{data.supervisor || 'Hardwire Office'}</span>
          <a href={data.supervisor_phone ? `tel:${data.supervisor_phone}` : BUSINESS.telephone} className="flex items-center gap-2 rounded-xl border border-slate-700 px-4 py-2 text-cyan-300" data-testid="call-supervisor"><Phone size={15} /> Call</a>
        </div>
      </div>

      {/* Checklist */}
      <div className="hw-card p-5">
        <div className="mb-4 flex items-center gap-2"><ClipboardCheck size={18} className="text-cyan-400" /><span className="font-head text-xl font-bold text-white">Checklist</span></div>
        <div className="space-y-2">
          {tasks.map((t, i) => (
            <button key={i} onClick={() => toggle(i)} className={`flex w-full items-center gap-3 rounded-xl border p-4 text-left transition ${t.done ? "border-emerald-500/40 bg-emerald-500/10" : "border-slate-700 bg-slate-900/60"}`} data-testid={`task-${i}`}>
              <div className={`flex h-7 w-7 shrink-0 items-center justify-center rounded-lg ${t.done ? "bg-emerald-500 text-slate-900" : "border border-slate-600"}`}>
                {t.done && <Check size={16} strokeWidth={3} />}
              </div>
              <span className={`font-medium ${t.done ? "text-emerald-200 line-through" : "text-slate-200"}`}>{t.label}</span>
            </button>
          ))}
        </div>
      </div>

      {/* Materials */}
      <div className="hw-card p-5">
        <div className="mb-3 flex items-center gap-2"><Package size={18} className="text-cyan-400" /><span className="font-head text-xl font-bold text-white">Materials</span></div>
        <div className="space-y-2">
          {(data.materials || []).map((m, i) => (
            <div key={i} className="flex items-center justify-between rounded-lg bg-slate-900/60 px-4 py-2.5">
              <span className="text-slate-200">{m.name}</span>
              <span className="font-mono2 text-cyan-300">×{m.qty}</span>
            </div>
          ))}
        </div>
      </div>

      <FileWorkspace jobId={id} dark />

      {/* Field change */}
      {coOpen ? (
        <div className="hw-card p-5">
          <div className="mb-3 flex items-center justify-between">
            <span className="font-head text-lg font-bold text-white">New Field Change</span>
            <button onClick={() => setCoOpen(false)}><X size={18} className="text-slate-500" /></button>
          </div>
          <textarea className="hw-input mb-3" rows={3} placeholder="Describe the extra work requested…" value={coDesc} onChange={(e) => setCoDesc(e.target.value)} data-testid="co-desc" />
          <input className="hw-input mb-3" placeholder="Est. price ($)" value={coAmt} onChange={(e) => setCoAmt(e.target.value)} data-testid="co-amount" />
          <button onClick={submitCO} className="glove-btn w-full bg-amber-500 text-slate-900" data-testid="co-submit">Send to PM</button>
        </div>
      ) : (
        data.lot_id && (
          <button onClick={() => setCoOpen(true)} className="glove-btn flex w-full items-center justify-center gap-2 border border-amber-500/40 bg-amber-500/10 text-amber-300" data-testid="create-field-change">
            <FileSignature size={18} /> Create Field Change
          </button>
        )
      )}

      {/* Submit */}
      <div className="sticky bottom-24 space-y-3">
        <button onClick={() => save("in_progress")} className="glove-btn w-full border border-slate-600 bg-slate-800 text-white" data-testid="save-progress">Save Progress</button>
        <button
          onClick={() => save("submitted")}
          disabled={!allDone}
          className="glove-btn flex w-full items-center justify-center gap-2 bg-emerald-500 text-slate-900 disabled:opacity-40"
          data-testid="submit-job"
        >
          <Send size={18} /> {allDone ? "Submit Job" : "Complete all tasks to submit"}
        </button>
      </div>
    </div>
  );
}
